import { useEffect, useState } from 'react';
import {
  addItem,
  addPay,
  advanceChecked,
  applyPresetChecked,
  applyTemplate,
  archiveDeal,
  deleteItem,
  delPay,
  fetchDocHtml,
  genDoc,
  getCatalog,
  getDeal,
  getTemplates,
  listDocs,
  listPayPresets,
  markPay,
  openDocWindow,
  patchDeal,
  patchItem,
  patchPay,
  restoreDeal,
  type CatalogItem,
  type DealFull,
  type DocKind,
  type DocRow,
  type Pay,
  type PayPreset,
  type StageBlock,
  type Template,
} from './deals-api';
import { setCehProg, setOtk } from './prodpay-api';
import { fmtDate, mln, money, splitByPct } from './format';
import { useCehs } from './cehs';
import { StageBlockModal } from './StageBlockModal';
import { PayMarkModal } from './PayMarkModal';
import { PctBar } from './PctBar';

const ALL_TABS = [
  ['items', 'Состав'],
  ['pay', 'Платежи'],
  ['docs', 'Документы'],
  ['prod', 'Производство'],
  ['ship', 'Отгрузка'],
  ['note', 'Контакт · заметки'],
] as const;
type Tab = (typeof ALL_TABS)[number][0];
const DOC_LABEL: Record<DocKind, string> = {
  kp: 'КП',
  contract: 'Договор',
  invoice: 'Счёт',
  waybill: 'Накладная',
};
const THREAD_LABEL: Record<string, string> = {
  pay: 'Оплата',
  supply: 'Закуп',
  prod: 'Производство',
  ship: 'Отгрузка',
};

export function DealModal({
  dealId,
  screens,
  onClose,
  onChanged,
  initialTab = 'items',
}: {
  dealId: number;
  screens: string[];
  onClose: () => void;
  onChanged: () => void;
  initialTab?: Tab;
}) {
  const [deal, setDeal] = useState<DealFull | null>(null);
  const [tab, setTab] = useState<Tab>(initialTab);
  const [catalog, setCatalog] = useState<CatalogItem[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [presets, setPresets] = useState<PayPreset[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [docs, setDocs] = useState<DocRow[]>([]);
  const [noteDraft, setNoteDraft] = useState('');
  const [contactDraft, setContactDraft] = useState({ name: '', phone: '' });
  const [presetSel, setPresetSel] = useState<string | null>(null); // выбранный пресет — предпросмотр
  const [stageBlock, setStageBlock] = useState<StageBlock | null>(null); // окно «аванс не оплачён»
  const [markRow, setMarkRow] = useState<Pay | null>(null); // транш, для которого открыто окно оплаты
  const cehs = useCehs();

  const archived = !!deal?.archived_at;
  const canManage = screens.includes('deals'); // право архивировать/восстанавливать (без блокировки архивом)
  // Архивная сделка — только для чтения: правки доступны после восстановления.
  const canEdit = canManage && !archived;
  const canPay = screens.includes('pay') && !archived;
  const canProd = screens.includes('prod') && !archived;
  const canShip = screens.includes('ship') && !archived;
  const financeHidden = deal?.financeHidden ?? false;
  // «Финансы скрыты от цехов»: у производственных ролей нет вкладок «Платежи» и «Документы».
  const TABS = ALL_TABS.filter(([k]) => !(financeHidden && (k === 'pay' || k === 'docs')));

  const reloadDocs = () =>
    listDocs(dealId)
      .then((r) => setDocs(r.docs))
      .catch(() => {});
  useEffect(() => {
    if (tab === 'docs') reloadDocs();
  }, [tab, dealId]);

  async function makeDoc(kind: DocKind) {
    setBusy(true);
    setMsg(null);
    try {
      const { html } = await genDoc(dealId, kind);
      openDocWindow(html);
      await reloadDocs();
      setDeal(await getDeal(dealId));
      onChanged();
    } catch {
      setMsg('Не удалось сформировать документ (нет прав или ошибка).');
    } finally {
      setBusy(false);
    }
  }
  async function openDoc(d: DocRow) {
    openDocWindow(await fetchDocHtml(dealId, d.id));
  }

  useEffect(() => {
    getDeal(dealId).then((d) => {
      setDeal(d);
      setNoteDraft(d.note ?? '');
      setContactDraft({ name: d.contact_name ?? '', phone: d.contact_phone ?? '' });
      if (d.financeHidden && (tab === 'pay' || tab === 'docs')) setTab('items');
    });
    getCatalog().then((r) => setCatalog(r.catalog));
    getTemplates().then((r) => setTemplates(r.templates));
    if (screens.includes('pay'))
      listPayPresets()
        .then((r) => setPresets(r.presets))
        .catch(() => {});
    // tab намеренно не в зависимостях: сброс вкладки нужен только при смене сделки
  }, [dealId, screens]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function run(p: Promise<DealFull>) {
    setBusy(true);
    setMsg(null);
    try {
      setDeal(await p);
      onChanged();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Ошибка');
    } finally {
      setBusy(false);
    }
  }

  async function doAdvance() {
    setBusy(true);
    setMsg(null);
    try {
      // передаём текущий этап — оптимистичная блокировка от параллельного сдвига.
      const res = await advanceChecked(dealId, deal?.stage);
      if (res.status === 'blocked') {
        setStageBlock(res.block); // аванс/просрочка — показываем окно
      } else if (res.status === 'conflict') {
        setMsg('Этап уже изменил другой пользователь — карточка обновлена.');
        setDeal(await getDeal(dealId));
      } else {
        setDeal(res.deal);
        onChanged();
      }
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Этап не двигается (правило контроля оплат).');
      getDeal(dealId)
        .then(setDeal)
        .catch(() => {});
    } finally {
      setBusy(false);
    }
  }

  async function doArchive() {
    if (
      !window.confirm(
        'Убрать сделку в архив? Она исчезнет из воронки и отчётов, но данные сохранятся — восстановить можно в один клик.',
      )
    )
      return;
    setBusy(true);
    setMsg(null);
    try {
      await archiveDeal(dealId);
      onChanged();
      onClose();
    } catch {
      setMsg('Не удалось убрать в архив.');
      setBusy(false);
    }
  }

  async function doRestore() {
    setBusy(true);
    setMsg(null);
    try {
      await restoreDeal(dealId);
      setDeal(await getDeal(dealId));
      onChanged();
    } catch {
      setMsg('Не удалось восстановить сделку.');
    } finally {
      setBusy(false);
    }
  }

  // Применить выбранный пресет; при наличии оплаченных траншей — подтверждение.
  async function applySelectedPreset(force = false) {
    if (!presetSel) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await applyPresetChecked(dealId, presetSel, force);
      if (res.status === 'needs_force') {
        setBusy(false);
        if (window.confirm('В плане есть оплаченные транши — пресет их сотрёт. Заменить план?')) {
          await applySelectedPreset(true);
        }
        return;
      }
      setDeal(res.deal);
      setPresetSel(null);
      onChanged();
    } catch {
      setMsg('Не удалось применить пресет.');
    } finally {
      setBusy(false);
    }
  }

  // Пока карточка грузится — сразу показываем оболочку окна (мгновенный отклик на клик),
  // а не пустоту. Раньше return null создавал ощущение «зависания» перед открытием.
  if (!deal)
    return (
      <div className="ovl" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-h">
            <div className="grow">
              <div className="m-cli">Загрузка…</div>
            </div>
            <button className="icon-btn close-x" onClick={onClose}>
              ✕
            </button>
          </div>
          <div className="mbody">
            <p className="muted">Загрузка карточки сделки…</p>
          </div>
        </div>
      </div>
    );
  const c = deal.computed;

  return (
    <>
      <div className="ovl" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-h">
            <div className="grow">
              <div className="m-no">{deal.no}</div>
              <div className="m-cli">{deal.client}</div>
              {(deal.region || deal.le) && (
                <div className="rmeta">{[deal.region, deal.le].filter(Boolean).join(' · ')}</div>
              )}
              {(deal.contact_name || deal.contact_phone) && (
                <div className="rmeta">
                  👤 {deal.contact_name || '—'}
                  {deal.contact_phone && (
                    <>
                      {' · '}
                      <a href={'tel:' + deal.contact_phone.replace(/[^\d+]/g, '')}>
                        📞 {deal.contact_phone}
                      </a>
                    </>
                  )}
                </div>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              {!financeHidden && <div className="m-sum">{money(deal.sum)}</div>}
              <div className={'chip ' + (archived ? 'red' : 'gold')} style={{ marginTop: 4 }}>
                {archived ? 'в архиве' : c.stageName}
              </div>
            </div>
            <button className="icon-btn close-x" onClick={onClose}>
              ✕
            </button>
          </div>

          <div style={{ padding: '10px 20px 0', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {!archived &&
              deal.flags.map((f, i) => (
                <span key={i} className={'chip ' + f.level}>
                  {f.text}
                </span>
              ))}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              {canEdit && deal.stage < c.stageCount - 1 && (
                <button className="btn sm" disabled={busy} onClick={doAdvance}>
                  Следующий этап →
                </button>
              )}
              {canManage &&
                (archived ? (
                  <button className="btn sm" disabled={busy} onClick={doRestore}>
                    ↩ Восстановить
                  </button>
                ) : (
                  <button
                    className="btn ghost sm"
                    disabled={busy}
                    onClick={doArchive}
                    title="Мягкое удаление — в архив"
                  >
                    🗄 В архив
                  </button>
                ))}
            </div>
          </div>
          {archived && (
            <p className="muted small" style={{ padding: '8px 20px 0', margin: 0 }}>
              Сделка в архиве и не участвует в воронке и отчётах. Правки и движение по этапам
              доступны после восстановления.
            </p>
          )}

          {/* 4 нити исполнения: оплата · закуп · производство · отгрузка */}
          <div className="threads-strip">
            {(['pay', 'supply', 'prod', 'ship'] as const).map((k) => (
              <div key={k} className="thr-item">
                <div className="thr-label">{THREAD_LABEL[k]}</div>
                <div className={'thread ' + (deal.threads[k] || '')} />
              </div>
            ))}
          </div>
          {msg && (
            <p className="err" style={{ padding: '8px 20px 0' }}>
              {msg}
            </p>
          )}

          <div className="mtabs">
            {TABS.map(([k, name]) => (
              <button
                key={k}
                className={'mtab' + (tab === k ? ' on' : '')}
                onClick={() => setTab(k)}
              >
                {name}
              </button>
            ))}
          </div>

          <div className="mbody">
            {tab === 'items' && (
              <>
                {deal.items.map((it) => (
                  <div className="row" key={it.id}>
                    <div className="grow">
                      <div className="rname">{it.name}</div>
                      <div className="rmeta">
                        {it.cat} · {cehs.short(it.ceh_id ?? '')}
                      </div>
                    </div>
                    {canEdit ? (
                      <div className="qty">
                        <button disabled={busy} onClick={() => run(patchItem(deal.id, it.id, -1))}>
                          −
                        </button>
                        <b>{it.qty}</b>
                        <button disabled={busy} onClick={() => run(patchItem(deal.id, it.id, 1))}>
                          +
                        </button>
                      </div>
                    ) : (
                      <b>{it.qty}×</b>
                    )}
                    <div className="price">{money(it.price * it.qty)}</div>
                    {canEdit && (
                      <button
                        className="icon-btn"
                        disabled={busy}
                        onClick={() => run(deleteItem(deal.id, it.id))}
                      >
                        🗑
                      </button>
                    )}
                  </div>
                ))}
                {!deal.items.length && <p className="muted">Состав пуст.</p>}
                {!financeHidden && deal.items.length > 0 && (
                  <div className="row" style={{ fontWeight: 800 }}>
                    <div className="grow">Итого контракт</div>
                    <div className="price m-sum">{money(deal.sum)}</div>
                  </div>
                )}

                {canEdit && (
                  <>
                    <div className="section-t">Добавить из каталога</div>
                    <div className="grid-cards">
                      {catalog.map((cat) => (
                        <button
                          key={cat.id}
                          className="catcard"
                          style={{ textAlign: 'left', cursor: 'pointer' }}
                          disabled={busy}
                          onClick={() => run(addItem(deal.id, cat.id))}
                        >
                          <div className="ci">{cat.ico}</div>
                          <div className="cn">{cat.name}</div>
                          <div className="cp">{mln(cat.price)} ₸</div>
                        </button>
                      ))}
                    </div>
                    <div className="section-t">Шаблоны комплексов</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {templates.map((t) => (
                        <button
                          key={t.id}
                          className="btn ghost sm"
                          disabled={busy}
                          onClick={() => run(applyTemplate(deal.id, t.id))}
                          title={t.descr}
                        >
                          {t.name}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}

            {tab === 'pay' && (
              <>
                <div className="row">
                  <div className="grow">
                    <b>Оплачено {c.paidPct}%</b> · осталось{' '}
                    <span className="due">{money(c.dueSum)}</span>
                  </div>
                  <div className="bar" style={{ width: 160 }}>
                    <span style={{ width: c.paidPct + '%' }} />
                  </div>
                </div>
                {deal.pay.map((p) => {
                  const partial = !p.paid && p.paid_amount > 0; // получена часть
                  if (canPay) {
                    return (
                      <div className="row" key={p.id}>
                        {!p.paid ? (
                          <>
                            <input
                              className="grow"
                              defaultValue={p.label}
                              disabled={busy}
                              onBlur={(e) =>
                                e.target.value !== p.label &&
                                run(patchPay(deal.id, p.id, { label: e.target.value }))
                              }
                            />
                            <input
                              type="number"
                              style={{ width: 64 }}
                              defaultValue={p.pct}
                              disabled={busy}
                              onBlur={(e) =>
                                +e.target.value !== p.pct &&
                                run(patchPay(deal.id, p.id, { pct: +e.target.value }))
                              }
                              title="% от суммы"
                            />
                            <input
                              type="number"
                              style={{ width: 64 }}
                              defaultValue={p.due_offset}
                              disabled={busy}
                              onBlur={(e) =>
                                +e.target.value !== p.due_offset &&
                                run(patchPay(deal.id, p.id, { due_offset: +e.target.value }))
                              }
                              title="срок: дней от сегодня (пере-штампует дату)"
                            />
                          </>
                        ) : (
                          <div className="grow">
                            <div className="rname">{p.label}</div>
                            <div className="rmeta">
                              {p.pct}% · срок {fmtDate(p.due_date)}
                              {p.paid_ref ? ' · ' + p.paid_ref : ''}
                            </div>
                          </div>
                        )}
                        <div className="price">
                          {money(p.amount)}
                          {partial && (
                            <div className="rmeta" style={{ color: 'var(--gold)' }}>
                              получено {money(p.paid_amount)}
                            </div>
                          )}
                        </div>
                        {p.paid ? (
                          <span className="chip grn">оплачено</span>
                        ) : partial ? (
                          <span className="chip gold">частично</span>
                        ) : null}
                        {!p.paid && (
                          <button className="btn sm" disabled={busy} onClick={() => setMarkRow(p)}>
                            Оплата
                          </button>
                        )}
                        {p.paid_amount > 0 && (
                          <button
                            className="btn ghost sm"
                            disabled={busy}
                            title="Откатить отметку оплаты"
                            onClick={() => run(markPay(deal.id, p.id, { paid: false }))}
                          >
                            Откат
                          </button>
                        )}
                        {!p.paid && p.paid_amount === 0 && (
                          <button
                            className="icon-btn"
                            disabled={busy}
                            title="Удалить транш"
                            onClick={() => run(delPay(deal.id, p.id))}
                          >
                            🗑
                          </button>
                        )}
                      </div>
                    );
                  }
                  return (
                    <div className="row" key={p.id}>
                      <div className="grow">
                        <div className="rname">{p.label}</div>
                        <div className="rmeta">
                          {p.pct}% · срок {fmtDate(p.due_date)}
                        </div>
                      </div>
                      <div className="price">{money(p.amount)}</div>
                      {p.paid ? (
                        <span className="chip grn">оплачено</span>
                      ) : partial ? (
                        <span className="chip gold">частично · {money(p.paid_amount)}</span>
                      ) : (
                        <span className="chip red">не оплачено</span>
                      )}
                    </div>
                  );
                })}
                {canPay && (
                  <>
                    {(() => {
                      const total = deal.pay.reduce((s, p) => s + p.pct, 0);
                      return (
                        <div className="row">
                          <div className="grow rmeta">
                            Сумма долей:{' '}
                            <b style={{ color: total === 100 ? 'var(--grn)' : 'var(--red)' }}>
                              {total}%
                            </b>
                            {total !== 100 && ' (норма 100%)'}
                          </div>
                          <button
                            className="btn ghost sm"
                            disabled={busy}
                            onClick={() => run(addPay(deal.id))}
                          >
                            + Транш
                          </button>
                        </div>
                      );
                    })()}
                    <div className="section-t">Пресет плана платежей</div>
                    {presets.length === 0 ? (
                      <p className="muted small">
                        Пресеты не заданы. Создайте их в разделе «Настройки».
                      </p>
                    ) : (
                      <>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {presets.map((p) => (
                            <button
                              key={p.id}
                              className={'btn sm' + (presetSel === p.key ? '' : ' ghost')}
                              disabled={busy}
                              onClick={() => setPresetSel(presetSel === p.key ? null : p.key)}
                            >
                              {p.name}
                            </button>
                          ))}
                        </div>

                        {(() => {
                          const sel = presets.find((p) => p.key === presetSel);
                          if (!sel) return null;
                          const amounts = splitByPct(
                            deal.sum,
                            sel.items.map((i) => i.pct),
                          );
                          return (
                            <div className="glass pad" style={{ marginTop: 10 }}>
                              <div className="rmeta" style={{ marginBottom: 8 }}>
                                Предпросмотр для этой сделки ({money(deal.sum)}):
                              </div>
                              <PctBar
                                items={sel.items.map((i) => ({ pct: i.pct, label: i.label }))}
                              />
                              <div style={{ marginTop: 10 }}>
                                {sel.items.map((it, i) => (
                                  <div className="row" key={i} style={{ cursor: 'default' }}>
                                    <div className="grow">
                                      <div className="rname">{it.label}</div>
                                      <div className="rmeta">
                                        {it.pct}% · срок {it.due_offset >= 0 ? '+' : ''}
                                        {it.due_offset} дн
                                      </div>
                                    </div>
                                    <div className="price">{money(amounts[i])}</div>
                                  </div>
                                ))}
                              </div>
                              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                <button
                                  className="btn sm"
                                  disabled={busy}
                                  onClick={() => applySelectedPreset()}
                                >
                                  Применить пресет
                                </button>
                                <button
                                  className="btn ghost sm"
                                  disabled={busy}
                                  onClick={() => setPresetSel(null)}
                                >
                                  Отмена
                                </button>
                                <span className="grow" />
                                <span className="rmeta">заменит весь план платежей</span>
                              </div>
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </>
                )}
              </>
            )}

            {tab === 'docs' && (
              <>
                <div className="rmeta" style={{ marginBottom: 10 }}>
                  Маршрут: продажи → оператор/юрист → директор → заказчик → бухгалтерия. Документы
                  собираются автоматически из состава и плана платежей.
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                  {canEdit && (
                    <>
                      <button className="btn sm" disabled={busy} onClick={() => makeDoc('kp')}>
                        Сформировать КП
                      </button>
                      <button
                        className="btn sm"
                        disabled={busy}
                        onClick={() => makeDoc('contract')}
                      >
                        Сформировать договор
                      </button>
                    </>
                  )}
                  {canPay && (
                    <button className="btn sm" disabled={busy} onClick={() => makeDoc('invoice')}>
                      Выставить счёт
                    </button>
                  )}
                  {canShip && (
                    <button className="btn sm" disabled={busy} onClick={() => makeDoc('waybill')}>
                      Накладная на отгрузку
                    </button>
                  )}
                </div>
                {docs.length === 0 ? (
                  <p className="muted">Документов пока нет.</p>
                ) : (
                  docs.map((d) => (
                    <div className="row" key={d.id}>
                      <div className="grow">
                        <div className="rname">
                          {DOC_LABEL[d.kind]} · {d.no}
                        </div>
                        <div className="rmeta">{d.created_at}</div>
                      </div>
                      <div className="price">{money(d.amount)}</div>
                      <button className="btn ghost sm" onClick={() => openDoc(d)}>
                        Открыть · печать
                      </button>
                    </div>
                  ))
                )}
              </>
            )}

            {tab === 'prod' && (
              <>
                <div className="row">
                  <div className="grow">
                    <b>Готовность производства {c.prodPct}%</b>
                  </div>
                  {deal.otk ? (
                    <span className="chip grn">ОТК ✓</span>
                  ) : (
                    <span className="chip muted">ОТК ожидается</span>
                  )}
                  {canProd && (
                    <button
                      className="btn ghost sm"
                      disabled={busy}
                      onClick={() => run(setOtk(deal.id, !deal.otk).then(() => getDeal(deal.id)))}
                    >
                      {deal.otk ? 'Снять ОТК' : 'Отметить ОТК'}
                    </button>
                  )}
                </div>

                {/* План-даты для Ганта (план-график ПДО). */}
                <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
                  <label className="field" style={{ flex: '1 1 160px' }}>
                    <span className="muted small">План: старт производства</span>
                    <input
                      type="date"
                      disabled={busy || !canProd}
                      defaultValue={deal.sched_start_date ?? ''}
                      onChange={(e) =>
                        run(patchDeal(deal.id, { sched_start_date: e.target.value || null }))
                      }
                    />
                  </label>
                  <label className="field" style={{ flex: '1 1 160px' }}>
                    <span className="muted small">План: отгрузка</span>
                    <input
                      type="date"
                      disabled={busy || !canProd}
                      defaultValue={deal.sched_ship_date ?? ''}
                      onChange={(e) =>
                        run(patchDeal(deal.id, { sched_ship_date: e.target.value || null }))
                      }
                    />
                  </label>
                </div>

                {cehs.list.map((ch) => (
                  <div className="row" key={ch.id}>
                    <div className="grow rname">{cehs.short(ch.id)}</div>
                    {canProd && (
                      <span className="qty">
                        <button
                          disabled={busy}
                          onClick={() =>
                            run(setCehProg(deal.id, ch.id, -25).then(() => getDeal(deal.id)))
                          }
                        >
                          −
                        </button>
                      </span>
                    )}
                    <div className="bar" style={{ width: 180 }}>
                      <span style={{ width: (deal.ceh[ch.id] ?? 0) + '%' }} />
                    </div>
                    {canProd && (
                      <span className="qty">
                        <button
                          disabled={busy}
                          onClick={() =>
                            run(setCehProg(deal.id, ch.id, 25).then(() => getDeal(deal.id)))
                          }
                        >
                          ＋
                        </button>
                      </span>
                    )}
                    <b style={{ width: 44, textAlign: 'right' }}>{deal.ceh[ch.id] ?? 0}%</b>
                  </div>
                ))}
                <p className="muted small" style={{ marginTop: 10 }}>
                  Детализация готовности по изделиям и штрихкоды — на экране «Производство».
                </p>
              </>
            )}

            {tab === 'ship' && (
              <>
                {deal.ship ? (
                  <>
                    <div className="row">
                      <div className="grow">
                        <b>Отгружено {c.shipPct}%</b> · машин: {deal.ship.trucks}
                      </div>
                    </div>
                    {deal.ship.items.map((s) => (
                      <div className="row" key={s.id}>
                        <div className="grow rname">{s.name}</div>
                        <div className="rmeta">
                          {s.qty_shipped}/{s.qty_total}
                        </div>
                        <div className="bar" style={{ width: 160 }}>
                          <span
                            style={{
                              width:
                                (s.qty_total
                                  ? Math.round((s.qty_shipped / s.qty_total) * 100)
                                  : 0) + '%',
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <p className="muted">
                    Отгрузка ещё не собрана. Управление — на экране «Отгрузка».
                  </p>
                )}
              </>
            )}

            {tab === 'note' && (
              <>
                <div className="section-t" style={{ marginTop: 0 }}>
                  Контакт
                </div>
                {canEdit ? (
                  <>
                    <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                      <input
                        className="grow"
                        style={{ minWidth: 160 }}
                        value={contactDraft.name}
                        disabled={busy}
                        placeholder="Контактное лицо"
                        onChange={(e) => setContactDraft((s) => ({ ...s, name: e.target.value }))}
                      />
                      <input
                        className="grow"
                        style={{ minWidth: 160 }}
                        type="tel"
                        value={contactDraft.phone}
                        disabled={busy}
                        placeholder="Телефон (+7 …)"
                        onChange={(e) => setContactDraft((s) => ({ ...s, phone: e.target.value }))}
                      />
                    </div>
                    <button
                      className="btn sm"
                      style={{ marginTop: 8 }}
                      disabled={
                        busy ||
                        (contactDraft.name === (deal.contact_name ?? '') &&
                          contactDraft.phone === (deal.contact_phone ?? ''))
                      }
                      onClick={() =>
                        run(
                          patchDeal(deal.id, {
                            contact_name: contactDraft.name,
                            contact_phone: contactDraft.phone,
                          }),
                        )
                      }
                    >
                      Сохранить контакт
                    </button>
                  </>
                ) : (
                  <p>
                    {deal.contact_name || deal.contact_phone ? (
                      [deal.contact_name, deal.contact_phone].filter(Boolean).join(' · ')
                    ) : (
                      <span className="muted">Контакт не указан.</span>
                    )}
                  </p>
                )}

                <p className="rmeta" style={{ margin: '16px 0 8px' }}>
                  Договор: {deal.contract_no ?? '—'} {deal.contract_signed ? '(подписан)' : ''}
                </p>
                {canEdit ? (
                  <>
                    <textarea
                      className="note-area"
                      value={noteDraft}
                      disabled={busy}
                      placeholder="Заметки по сделке…"
                      onChange={(e) => setNoteDraft(e.target.value)}
                    />
                    <button
                      className="btn sm"
                      disabled={busy || noteDraft === (deal.note ?? '')}
                      onClick={() => run(patchDeal(deal.id, { note: noteDraft }))}
                    >
                      Сохранить заметку
                    </button>
                  </>
                ) : (
                  <p>{deal.note || <span className="muted">Заметок нет.</span>}</p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {stageBlock && (
        <StageBlockModal
          block={stageBlock}
          dealNo={deal.no}
          canPay={canPay}
          onClose={() => setStageBlock(null)}
          onOpenPay={() => {
            setStageBlock(null);
            setTab('pay');
          }}
          onAcceptAdvance={
            canPay && stageBlock.advance
              ? async () => {
                  await markPay(dealId, stageBlock.advance!.pay_id, {});
                  const res = await advanceChecked(dealId, deal.stage);
                  setStageBlock(null);
                  if (res.status === 'ok') {
                    setDeal(res.deal);
                    onChanged();
                  } else {
                    setDeal(await getDeal(dealId));
                  }
                }
              : undefined
          }
        />
      )}
      {markRow && (
        <PayMarkModal
          dealNo={deal.no}
          label={markRow.label}
          amount={markRow.amount}
          paidAmount={markRow.paid_amount}
          paidRef={markRow.paid_ref}
          onClose={() => setMarkRow(null)}
          onSubmit={async (amount, ref) => {
            await run(markPay(deal.id, markRow.id, { amount, ref }));
            setMarkRow(null);
          }}
        />
      )}
    </>
  );
}
